<template>
    <div class="p-0 m-0">
        <DTable
            :items="invoices"
            :fields="fields"
            :sorter="isSortable"
            :sorter-value="sorterValue"
            class="table-responsive"
            @update:sorter-value="updateOrdering"
        >
            <template #id="{item}">
                <td v-if="userPermissions.includes('invoice.view')">
                    <Link :href="showInvoiceRoute(item)">
                        {{ item.id }}
                    </Link>
                </td>
                <td v-else>
                    {{ item.id }}
                </td>
            </template>
            <template #customer="{item}">
                <td v-if="userPermissions.includes('customer.view')">
                    <Link :href="customerInvoicesRoute(item)">
                        {{ item.customer }}
                    </Link>
                </td>
                <td v-else>
                    {{ item.customer }}
                </td>
            </template>
            <template #total_amount="{item}">
                <td
                    :class="{'table-text-color-red': item.total_amount < 0}"
                >
                    {{ priceFormatForDisplay(item.total_amount, 2, item.customer_market_region_id) }}
                </td>
            </template>
            <template #open_item_amount="{item}">
                <td
                    v-if="item.open_item_amount"
                    :class="{'table-text-color-red': item.open_item_amount < 0}"
                >
                    {{ priceFormatForDisplay(item.open_item_amount, 2, item.customer_market_region_id) }}
                </td>
                <td v-else />
            </template>
            <template #type="{item}">
                <td>{{ __('customers.contacts.invoice_type.' + item.type) }}</td>
            </template>
            <template #status="{item}">
                <td>
                    <div>
                        {{ getInvoiceStatus(item.force_status_paid, item.is_settled, item.status) }}
                    </div>
                </td>
            </template>
            <template #dunning_status="{item}">
                <td>
                    <div v-if="item.dunning_status && item.in_lawsuit === false">
                        {{ __('settings.dunning_pdf_settings.types.' + item.dunning_status) }}
                    </div>
                    <div v-if="item.dunning_status && item.in_lawsuit === true">
                        {{ __('customers.invoices.in_lawsuit') }}
                    </div>
                    <strong v-if="item.sepa_chargeback === true">
                        <br>
                        {{ __('customers.invoices.chargeback') }}
                    </strong>
                    <strong v-if="item.debt_loss === true">
                        <br>
                        {{ __('customers.invoices.debt_loss') }}
                    </strong>
                </td>
            </template>
            <template #debt_loss="{item}">
                <td>
                    <span v-if="item.debt_loss">
                        {{ setDateFormat(item.debt_loss_date) }}
                    </span>
                </td>
            </template>
            <template #due_date="{item}">
                <td>
                    {{ setDateFormatEuropeBerlin(item.due_date) }}
                </td>
            </template>
            <template #date="{item}">
                <td>
                    {{ setDateFormatEuropeBerlin(item.date) }}
                </td>
            </template>
            <template #actions="{item}">
                <td>
                    <DDropdown
                        :id="`actions_dropdown_${item.id}`"
                        add-menu-classes="pt-0 pb-0"
                        :toggler-text="__('messages.actions.actions')"
                    >
                        <DDropdownItem
                            :id="`action_download_${item.id}`"
                            :href="getDownloadInvoiceLink(item)"
                        >
                            <div class="row pl-1 pr-1">
                                <DIcon
                                    name="download"
                                    :size="18"
                                    class="pr-1"
                                />
                                <div>
                                    {{ item.type.startsWith('invoice')
                                        ? __('customers.invoices.invoice')
                                        : __('customers.credit_notes.credit_note')
                                    }}
                                </div>
                            </div>
                        </DDropdownItem>
                        <DDropdownItem
                            v-if="item.has_e_invoice"
                            :id="`action_download_e_invoice_xml_${item.id}`"
                            :href="getDownloadEInvoiceXmlLink(item)"
                        >
                            <div class="row pl-1 pr-1">
                                <DIcon
                                    name="download"
                                    :size="18"
                                    class="pr-1"
                                />
                                <div>
                                    {{ __('customers.invoices.e_invoice_xml') }}
                                </div>
                            </div>
                        </DDropdownItem>
                        <DDropdownItem
                            v-for="(dunning, index) in item.dunning_letters"
                            :id="`dunning_download_${item.id}_${dunning.name}`"
                            :key="index"
                            :href="getDownloadDunningLink(item, dunning.id)"
                        >
                            <div class="row pl-1 pr-1">
                                <DIcon
                                    name="download"
                                    :size="18"
                                    class="pr-1"
                                />
                                <div>
                                    {{ dunning.name }}
                                </div>
                            </div>
                        </DDropdownItem>
                        <DDropdownItem
                            v-if="item.type.startsWith('invoice') && userPermissions.includes('invoice.resend_document')"
                            :id="`action_resend_${item.id}`"
                            :href="resendInvoiceEmail(item)"
                        >
                            <div class="row pl-1 pr-1">
                                <DIcon
                                    name="redo"
                                    :size="18"
                                    class="pr-1"
                                />
                                <div>
                                    {{ __(('tools.invoices.resend_invoice.' + item.customer_delivery_type)) }}
                                </div>
                            </div>
                        </DDropdownItem>
                        <DDropdownItem
                            v-if="item.customer.invoice_csv"
                            :id="`action_download_csv_${item.id}`"
                            :href="getDownloadInvoiceCsvLink(item)"
                        >
                            <div class="row pl-1 pr-1">
                                <DIcon
                                    name="download"
                                    :size="18"
                                    class="pr-1"
                                />
                                <div>
                                    {{ __('customers.invoices.invoice_csv') }}
                                </div>
                            </div>
                        </DDropdownItem>
                        <DDropdownItem
                            v-if="item.type.startsWith('invoice')
                                && item.status === 'unpaid'
                                && userPermissions.includes('invoice.set_debt_loss')
                                && !item.debt_loss"
                            :id="`action_set_debt_loss_${item.id}`"
                            @click="openSetDebtLossModal(item)"
                        >
                            <div class="row pl-1 pr-1">
                                <DIcon
                                    name="delete"
                                    :size="18"
                                    class="pr-1"
                                />
                                <div>
                                    {{ __('tools.invoices.set_debt_loss') }}
                                </div>
                            </div>
                        </DDropdownItem>
                        <DDropdownItem
                            v-if="item.force_status_paid && userPermissions.includes('invoice.align_payment_status')"
                            :id="`action_align_payment_status_${item.id}`"
                            :href="alignPaymentStatus(item)"
                        >
                            <div class="row pl-1 pr-1">
                                <DIcon
                                    name="redo"
                                    :size="18"
                                    class="pr-1"
                                />
                                <div>
                                    {{ __('tools.invoices.align_payment_status') }}
                                </div>
                            </div>
                        </DDropdownItem>
                    </DDropdown>
                </td>
            </template>
        </DTable>
        <SetDebtLossModal
            v-if="showSetDebtLossModal"
            :show-modal="showSetDebtLossModal"
            :invoice="selectedInvoice"
            @close-modal="closeSetDebtLossModal()"
        />
    </div>
</template>

<script>

import moment from 'moment/moment'
import { __ } from '~@/Composables/translation'
import priceFormat from '~@/Mixins/priceFormat'
import SetDebtLossModal from '~@/Components/Customers/Invoices/SetDebtLossModal.vue'
export default {
    name: 'InvoiceIndex',
    components: { SetDebtLossModal },
    mixins: [
        priceFormat
    ],
    props: {
        invoices: {
            type: Array,
            default: () => {
                return []
            }
        },
        withCustomer: {
            type: Boolean,
            default: () => {
                return false
            }
        },
        sorterValue: {
            type: Object,
            default: () => {
                return {}
            }
        },
        fields: {
            type: Array,
            default: () => {
                return []
            }
        }
    },
    emits: ['update:ordering'],
    data () {
        return {
            selectedInvoice: {},
            showSetDebtLossModal: false
        }
    },
    computed: {
        isSortable () {
            if (!this.sorterValue.column) return false

            return { external: true }
        },
        userPermissions () {
            return this.$page.props.auth.permissions
        }
    },
    methods: {
        showInvoiceRoute (invoice) {
            return invoice.type.startsWith('invoice')
                ? this.$route('customers.customers.invoices.show', {
                    customer: invoice.customer_id,
                    invoice: invoice.id
                })
                : this.$route('customers.customers.credit-notes.show', {
                    customer: invoice.customer_id,
                    credit_note: invoice.id
                })
        },
        customerInvoicesRoute (invoice) {
            return this.$route('customers.customers.invoices.index', { customer: invoice.customer_id })
        },
        getDownloadInvoiceLink (invoice) {
            if (invoice.type.startsWith('invoice')) {
                return this.$route('tools.invoices.download', {
                    customer: invoice.customer_id,
                    invoice: invoice.id
                })
            }

            return this.$route('customers.customers.credit-notes.download', {
                customer: invoice.customer_id,
                credit_note: invoice.id
            })
        },
        getDownloadEInvoiceXmlLink (invoice) {
            if (invoice.type === 'invoice' || invoice.type === 'invoice_cancellation') {
                return this.$route('tools.invoices.download-einvoice-xml', {
                    invoice: invoice.id
                })
            }
            if (invoice.type === 'credit_note' || invoice.type === 'credit_note_cancellation') {
                return this.$route('tools.credit-notes.download-einvoice-xml', {
                    credit_note: invoice.id
                })
            }
        },
        getDownloadDunningLink (invoice, dunningId) {
            return this.$route('tools.dunning.download', { dunning: dunningId })
        },
        updateOrdering (event) {
            this.$emit('update:ordering', { column: event.column, asc: event.asc })
        },
        resendInvoiceEmail (invoice) {
            return this.$route('tools.invoices.resend', {
                invoice: invoice.id
            })
        },
        getDownloadInvoiceCsvLink (invoice) {
            return this.$route('tools.invoices.download-csv', {
                invoice: invoice.id
            })
        },
        alignPaymentStatus (invoice) {
            return this.$route('tools.invoices.align-payment-status', {
                invoice: invoice.id
            })
        },
        toDateTimeString (value) {
            return moment(value).format('YYYY-MM-DD')
        },
        getInvoiceStatus (forcePaid, isSettled, status) {
            let additionalText = ''

            if (forcePaid === false && status === 'paid' && isSettled === false) {
                additionalText = __('tools.invoices.divergent')
            }

            if (forcePaid === true && status === 'paid' && isSettled === false) {
                additionalText = __('tools.invoices.forced_divergent')
            }

            if (forcePaid === false && status === 'unpaid' && isSettled === true) {
                additionalText = __('tools.invoices.divergent')
            }

            if (forcePaid === true && status === 'unpaid' && isSettled === true) {
                additionalText = __('tools.invoices.forced_divergent')
            }

            if (additionalText !== '') {
                return __('tools.dunning.invoice_status.' + status) + ' (' + additionalText + ')'
            }

            return __('tools.dunning.invoice_status.' + status)
        },
        openSetDebtLossModal (invoice) {
            this.selectedInvoice = invoice
            this.showSetDebtLossModal = true
        },
        closeSetDebtLossModal () {
            this.selectedInvoice = {}
            this.showSetDebtLossModal = false
        }
    }
}
</script>
